import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCertificateReview, getCertificateStats, pushCertificateRegister, annotateCertificate } from '../api';
import type { CertificateReviewResponse, CertificateStatsResponse } from '../types';

/**
 * TanStack Query hooks for the Certificate Validation page. The Review and
 * Statistics tabs keep INDEPENDENT date ranges and therefore independent query
 * keys (spec §6.1) — nothing is shared between them.
 */
export const certificateValidationKeys = {
  all: ['certificate-validation'] as const,
  review: (from?: string, to?: string) => [...certificateValidationKeys.all, 'review', { from, to }] as const,
  stats: (from?: string, to?: string) => [...certificateValidationKeys.all, 'stats', { from, to }] as const,
};

/**
 * Review triage for the given range. Manual-trigger: pass `enabled=false` until
 * the reviewer clicks "Analyser" (spec §5.1 "rien ne part avant le clic").
 */
export function useCertificateReview(from: string | undefined, to: string | undefined, enabled: boolean) {
  return useQuery<CertificateReviewResponse>({
    queryKey: certificateValidationKeys.review(from, to),
    queryFn: () => getCertificateReview({ from: from as string, to }),
    enabled: enabled && !!from,
    staleTime: 60 * 1000,
  });
}

/** Statistics over the given range. Enabled only while the Statistics tab is mounted. */
export function useCertificateStats(from: string | undefined, to: string | undefined, enabled: boolean) {
  return useQuery<CertificateStatsResponse>({
    queryKey: certificateValidationKeys.stats(from, to),
    queryFn: () => getCertificateStats({ from: from as string, to }),
    enabled: enabled && !!from,
    staleTime: 5 * 60 * 1000,
  });
}

/** Server-authoritative register simulation shown before confirmation. */
export function useRegisterPreview() {
  return useMutation({
    mutationFn: (certs: string[]) => pushCertificateRegister({ certs, dryRun: true }),
  });
}

/** Live register write bound to the confirmed server preview. */
export function useRegisterPush() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ certs, confirmationToken }: { certs: string[]; confirmationToken: string }) => (
      pushCertificateRegister({ certs, dryRun: false, confirmationToken })
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: certificateValidationKeys.all }),
  });
}

/** Batch-annotate rejected certificates (best-effort per cert). */
export function useAnnotateRejections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: Array<{ id: string; comment: string }>) => {
      const results = await Promise.allSettled(items.map((it) => annotateCertificate(it.id, it.comment)));
      return { total: items.length, failed: results.filter((r) => r.status === 'rejected').length };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: certificateValidationKeys.all }),
  });
}
